import { collection, doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { useEffect, useState } from "react";
import { db } from "../../firebase";
import { Dashboard } from "../../models/Dashboard";
import { Button } from "../../design/button";
import { useNavigate } from "react-router-dom";
import {
  SparklesIcon,
  MagnifyingGlassIcon,
  ChartBarSquareIcon,
  BookOpenIcon,
  FireIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
} from "@heroicons/react/20/solid";

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState<Dashboard>();
  const navigate = useNavigate();

  useEffect(() => {
    const docRef = doc(collection(db, "dashboards"), user!.uid);
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        const data = { id: snapshot.id, ...snapshot.data() } as Dashboard;
        setDashboard(data);
      },
      (err) => {
        console.error(err);
      },
    );
    return () => {
      unsubscribe();
    };
  }, [user]);

  const quickLinks = [
    {
      title: "AI Valuation",
      description: "AI-powered price predictions with Monte Carlo simulations",
      icon: SparklesIcon,
      path: "/valuation",
      badge: "Popular",
    },
    {
      title: "Price Database",
      description: "Search 1M+ auction records with advanced analytics",
      icon: MagnifyingGlassIcon,
      path: "/auctionRecords",
    },
    {
      title: "Market Intelligence",
      description: "Real-time market trends, heat index, and emerging artists",
      icon: ChartBarSquareIcon,
      path: "/market",
      badge: "New",
    },
    {
      title: "API Documentation",
      description: "Complete API reference and integration guides",
      icon: BookOpenIcon,
      path: "/docs",
    },
  ];

  const marketStats = [
    {
      label: "Market Heat",
      value: "72/100",
      icon: FireIcon,
      trend: "+5.2%",
    },
    {
      label: "Avg Price Growth",
      value: "+8.7%",
      icon: ArrowTrendingUpIcon,
      trend: "Last 6mo",
    },
    {
      label: "Total Volume",
      value: "$125M",
      icon: CurrencyDollarIcon,
      trend: "+15.3%",
    },
    {
      label: "Emerging Artists",
      value: "127",
      icon: UserGroupIcon,
      trend: "20%+ growth",
      clickable: true,
    },
  ];

  return (
    <div className="min-h-screen bg-gallery-50">
      {/* Header */}
      <div className="bg-white border-b border-gallery-200">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <h1 className="font-display text-display-sm text-gallery-900 mb-2">
            Welcome back{user?.displayName ? `, ${user.displayName}` : ""}
          </h1>
          <p className="text-gallery-500">
            Your art market intelligence platform
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Market Stats Overview */}
        <div className="grid grid-cols-1 gap-4 mb-10 md:grid-cols-2 lg:grid-cols-4">
          {marketStats.map((stat, idx) => (
            <div
              key={idx}
              onClick={() => stat.clickable ? navigate("/emerging-artists") : undefined}
              className={`bg-white border border-gallery-200 rounded-sm p-6 ${
                stat.clickable ? "cursor-pointer hover:border-gallery-400 transition-colors" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-2.5 rounded-sm bg-gallery-100">
                  <stat.icon className="w-5 h-5 text-gallery-600" />
                </div>
                <span className="text-xs text-gallery-500 font-medium">
                  {stat.trend}
                </span>
              </div>
              <p className="text-sm text-gallery-500 mb-1">{stat.label}</p>
              <p className="stat-value text-2xl">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Quick Links */}
        <div className="mb-10">
          <h2 className="font-display text-lg text-gallery-900 mb-5">Quick Access</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {quickLinks.map((link, idx) => (
              <button
                key={idx}
                onClick={() => navigate(link.path)}
                className="group text-left bg-white border border-gallery-200 rounded-sm p-6 hover:border-gallery-400 transition-colors"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-2.5 rounded-sm bg-gallery-100 group-hover:bg-gallery-200 transition-colors">
                    <link.icon className="w-5 h-5 text-gallery-700" />
                  </div>
                  {link.badge && (
                    <span className="text-xs font-medium text-gallery-500 px-2 py-0.5 bg-gallery-100 rounded-sm">
                      {link.badge}
                    </span>
                  )}
                </div>
                <h3 className="font-medium text-gallery-900 mb-1.5 group-hover:text-gallery-700 transition-colors">
                  {link.title}
                </h3>
                <p className="text-sm text-gallery-500 leading-relaxed">
                  {link.description}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* API Usage Stats */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="bg-white border border-gallery-200 rounded-sm p-6 lg:col-span-2">
            <h2 className="font-display text-lg text-gallery-900 mb-6">Platform Capabilities</h2>
            <div className="space-y-4">
              {[
                {
                  title: "AI-Powered Valuations",
                  description: "Monte Carlo price predictions with 95% confidence intervals",
                  icon: SparklesIcon,
                },
                {
                  title: "Advanced Analytics",
                  description: "CAGR, Sharpe ratio, volatility, liquidity scoring",
                  icon: ChartBarSquareIcon,
                },
                {
                  title: "Market Intelligence",
                  description: "Real-time trends, sentiment, emerging artists",
                  icon: FireIcon,
                },
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 border border-gallery-100 rounded-sm">
                  <div className="p-2.5 rounded-sm bg-gallery-100 shrink-0">
                    <feature.icon className="w-5 h-5 text-gallery-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gallery-800">{feature.title}</p>
                    <p className="text-sm text-gallery-500">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gallery-200 rounded-sm p-6">
            <h2 className="font-display text-lg text-gallery-900 mb-6">API Usage</h2>
            <div className="h-px bg-gallery-100 mb-6" />
            <div className="mb-6">
              <p className="text-sm text-gallery-500 mb-2">Total Calls</p>
              <p className="font-display text-4xl text-gallery-900">
                {dashboard?.numberOfCalls?.toLocaleString() ?? "0"}
              </p>
            </div>
            <div className="space-y-3">
              <Button href="/usage" className="w-full">
                View Detailed Usage
              </Button>
              <Button href="/keys" outline className="w-full">
                Manage API Keys
              </Button>
            </div>
          </div>
        </div>

        {/* Getting Started CTA */}
        <div className="mt-10 bg-gallery-900 rounded-sm p-8">
          <div className="max-w-2xl">
            <h2 className="font-display text-2xl text-white mb-3">Ready to explore?</h2>
            <p className="text-gallery-300 mb-6 leading-relaxed">
              Start by getting an AI valuation for an artwork, or search our comprehensive
              price database to see market analytics in action.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button href="/valuation" color="white">
                Start Valuation
              </Button>
              <Button href="/auctionRecords" outline className="text-white border-gallery-600 hover:bg-gallery-800">
                Browse Database
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
